// VersionDiffPanel — generic field-level diff renderer.
//
// Takes a changeset (`{field: {old, new}}`) and a versioning config
// (financialFields, headlineFieldOrder) and renders the
// AggregateDeltaBanner + a sortable table of changed fields.
//
// Financial fields are highlighted at the top, then the rest in
// alphabetical order. Each row shows a coloured signed Δ when the
// field is financial.
import React from 'react';
import AggregateDeltaBanner, { formatDelta } from './AggregateDeltaBanner';

function fmtVal(v) {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return '[object]'; }
  }
  return String(v);
}

function DiffRow({ field, oldVal, newVal, isFinancial, testIdPrefix }) {
  const delta = isFinancial ? formatDelta(oldVal, newVal) : null;
  const deltaClass =
    delta?.sign === 'plus'  ? 'bg-emerald-50 text-emerald-700' :
    delta?.sign === 'minus' ? 'bg-rose-50 text-rose-700' :
    'text-slate-400';
  return (
    <div
      className="grid grid-cols-[1.2fr_1fr_1fr_0.9fr] gap-2 py-1.5 text-xs border-b border-slate-100 last:border-0 items-center"
      data-testid={`${testIdPrefix}-row-${field}`}
    >
      <div className="font-mono text-slate-600 truncate">{field}</div>
      <div className="line-through text-rose-600 truncate" title={fmtVal(oldVal)}>{fmtVal(oldVal)}</div>
      <div className="text-emerald-700 truncate" title={fmtVal(newVal)}>{fmtVal(newVal)}</div>
      <div className="text-left font-mono">
        {delta ? (
          <span
            className={`px-1.5 py-0.5 rounded font-medium ${deltaClass}`}
            data-testid={`${testIdPrefix}-delta-${field}`}
          >
            {delta.text}
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </div>
    </div>
  );
}

export default function VersionDiffPanel({ changeset, config, testIdPrefix = 'diff' }) {
  const keys = Object.keys(changeset || {});
  if (keys.length === 0) {
    return <div className="text-xs text-slate-400 italic py-2">لا يوجد فروقات — الإصدار الأصلي</div>;
  }
  const financialFields = config.financialFields;
  const sortedKeys = [...keys].sort((a, b) => {
    const af = financialFields.has(a) ? 0 : 1;
    const bf = financialFields.has(b) ? 0 : 1;
    if (af !== bf) return af - bf;
    return a.localeCompare(b);
  });
  return (
    <div className="bg-slate-50 rounded-lg p-3 mt-2">
      <AggregateDeltaBanner changeset={changeset} config={config} testIdPrefix={testIdPrefix} />
      <div className="grid grid-cols-[1.2fr_1fr_1fr_0.9fr] gap-2 text-xs font-medium text-slate-500 pb-1 border-b border-slate-200 mb-1">
        <div>الحقل</div>
        <div>السابق</div>
        <div>الجديد</div>
        <div>الفارق</div>
      </div>
      {sortedKeys.map((k) => (
        <DiffRow
          key={k}
          field={k}
          oldVal={changeset[k]?.old}
          newVal={changeset[k]?.new}
          isFinancial={financialFields.has(k)}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
}
